/**
 * Renders path lander/offer rows (weight, share %, row actions).
 * Row chrome aligned with FlowWeightedItemRow on the campaign flow page.
 */

import { useMemo, type FC } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRowLookup,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import {
  comparePreviewEntries,
  type PreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/preview-sort';

import { PATH_LIST_CONTAINER_CLASS } from '../../path-list-chrome';
import {
  getPathItemDisplay,
  type PathItemKind,
} from '../../../utils/path-item-display';
import PathItemRow from './PathItemRow';

export interface PathItemsRowsProps {
  items: unknown[];
  kind: PathItemKind;
  noun: 'lander' | 'offer';
  pathId: string;
  ariaLabel: string;
  totalWeight: number;
  itemWeightsLocked?: boolean;
  sort?: PreviewMetricSort | null;
  onSetWeight: (idx: number, weight: number) => void;
  onEdit: (idx: number) => void;
  onDuplicate: (idx: number) => void;
  onRemove: (idx: number, title: string) => void;
}

function readItemEntityId(row: unknown, kind: PathItemKind): string {
  const { id } = getPathItemDisplay(row, kind);
  return id === '—' ? '' : id;
}

const PathItemsRows: FC<PathItemsRowsProps> = ({
  items,
  kind,
  noun,
  pathId,
  ariaLabel,
  totalWeight,
  itemWeightsLocked = false,
  sort = null,
  onSetWeight,
  onEdit,
  onDuplicate,
  onRemove,
}) => {
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const entries = useMemo(() => {
    const mapped = items.map((item, index) => ({ item, index }));
    if (!previewMode) return mapped;
    return mapped.sort((a, b) =>
      comparePreviewEntries(
        {
          active: (a.item as { active?: boolean }).active !== false,
          name: getPathItemDisplay(a.item, kind).title,
          row: lookup(kind as FlowLayer, readItemEntityId(a.item, kind), pathId),
        },
        {
          active: (b.item as { active?: boolean }).active !== false,
          name: getPathItemDisplay(b.item, kind).title,
          row: lookup(kind as FlowLayer, readItemEntityId(b.item, kind), pathId),
        },
        sort,
      ),
    );
  }, [items, kind, pathId, previewMode, lookup, sort]);

  return (
    <ul className={cn(PATH_LIST_CONTAINER_CLASS)} aria-label={ariaLabel}>
      {entries.map(({ item, index }) => {
        const { title } = getPathItemDisplay(item, kind);
        return (
          <PathItemRow
            key={`${pathId}-${index}`}
            raw={item}
            kind={kind}
            noun={noun}
            pathId={pathId}
            totalWeight={totalWeight}
            itemWeightsLocked={itemWeightsLocked}
            onSetWeight={(w) => onSetWeight(index, w)}
            onEdit={() => onEdit(index)}
            onDuplicate={() => onDuplicate(index)}
            onRemove={() => onRemove(index, title)}
          />
        );
      })}
    </ul>
  );
};

export default PathItemsRows;
